import { useEffect, useRef } from 'react';
import type { LatLng } from '../pages/lite/visitTiming';

// JIM-1006-05: watch the phone's position while the inspection flow is open
// so the visit's arrival (and leaving) come from being at the site, not from
// opening the form "which sometimes happens when in route" (Jim). Never
// prompts on its own: it only watches when the permission is already
// granted, or when the browser cannot say (older WebKit) — the flow's other
// location features have asked by then.
export function useArrivalWatch(
  pin: LatLng | null,
  enabled: boolean,
  onFix: (here: LatLng, at: string) => void
) {
  const onFixRef = useRef(onFix);
  onFixRef.current = onFix;

  useEffect(() => {
    if (!enabled || !pin || typeof navigator === 'undefined') return;
    const geo = navigator.geolocation;
    if (!geo) return;
    let watchId: number | null = null;
    let cancelled = false;

    const start = () => {
      if (cancelled) return;
      watchId = geo.watchPosition(
        (position) => {
          onFixRef.current(
            {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            },
            new Date(position.timestamp || Date.now()).toISOString()
          );
        },
        () => {
          /* denied or unavailable: the form-open fallback stands */
        },
        { enableHighAccuracy: true, maximumAge: 15_000, timeout: 30_000 }
      );
    };

    const perms = navigator.permissions;
    if (perms?.query) {
      perms
        .query({ name: 'geolocation' })
        .then((status) => {
          if (status.state === 'granted') start();
          else if (status.state === 'prompt') {
            // Only start once something else has been granted the permission.
            status.onchange = () => {
              if (status.state === 'granted') start();
            };
          }
        })
        .catch(start);
    } else {
      start();
    }

    return () => {
      cancelled = true;
      if (watchId !== null) geo.clearWatch(watchId);
    };
    // pin coordinates are the identity that matters
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pin?.latitude, pin?.longitude]);
}
